import { useState, useRef } from 'react';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { getVideoDownloadUrl } from '@opm/services';
import { notification } from 'tea-component';
import useLanguage from '@op/hooks/useLanguage';

interface IUseVideoDownloadResult {
  videoUrls: string[];
  loading: boolean;
  error: boolean;
  activated: boolean;
  retryExhausted: boolean;
  fetchVideoUrl: () => void;
  retry: () => void;
}

/**
 * 获取视频下载链接的 hook
 * 点击播放按钮时调用 fetchVideoUrl 触发请求
 * @param videoId - 视频ID
 */
export function useVideoDownload(videoId: string): IUseVideoDownloadResult {
  const [videoUrls, setVideoUrls] = useState<string[]>([]);
  const [activated, setActivated] = useState(false);
  const [retryExhausted, setRetryExhausted] = useState(false);
  const videoIdRef = useRef<string>(videoId);
  const retryCountRef = useRef(0);
  const fetchingRef = useRef(false);
  const MAX_RETRY = 3;
  const { isEn } = useLanguage();

  const { wrapperCancelRequest, loading, error } = useCancelRequestHook({
    defaultLoading: false,
    clear: () => setVideoUrls([]),
  });

  const doFetch = (): void => {
    if (!videoId) {
      return;
    }

    fetchingRef.current = true;
    videoIdRef.current = videoId;

    wrapperCancelRequest(getVideoDownloadUrl, { video_id: videoId })
      .then(response => {
        fetchingRef.current = false;
        // 确保响应对应当前的 videoId
        if (videoIdRef.current === videoId) {
          // 合并所有可用的视频链接
          const downloadUrls = response?.video_download_urls || [];
          const playUrls = response?.video_play_urls || [];
          const allUrls = [...downloadUrls, ...playUrls].filter(Boolean);
          setVideoUrls(allUrls);
          // 请求成功，设置 activated 并重置重试计数
          setActivated(true);
          retryCountRef.current = 0;
        }
      })
      .catch(() => { 
        fetchingRef.current = false;
        // 请求失败，如果未超过重试次数则重试
        if (
          retryCountRef.current < MAX_RETRY &&
          videoIdRef.current === videoId
        ) {
          retryCountRef.current += 1;
          doFetch();
        } else {
          // 重试次数用尽
          setRetryExhausted(true);
          notification.error({
            description: isEn
              ? 'Video unavailable. Swipe for next.'
              : '当前视频暂不支持播放，请滑动查看下一个视频',
          });
        }
      });
  };

  const fetchVideoUrl = (): void => {
    if (activated || fetchingRef.current) {
      return;
    }
    doFetch();
  };

  const retry = (): void => {
    retryCountRef.current = 0;
    setRetryExhausted(false);
    setActivated(false);
    fetchingRef.current = false;
    doFetch();
  };

  return {
    videoUrls,
    loading,
    error,
    activated,
    retryExhausted,
    fetchVideoUrl,
    retry,
  };
}
